import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Alert, CircularProgress, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
} from '@mui/material';

import { backupsAPI, usersAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const STATUS_COLORS = {
  green: '#E4FEDB', orange: '#FFC300', red: '#FF7277', yellow: '#FFFF00',
  not_required: '#E0E0E0', missing: '#FF7277',
};
const STATUS_LABELS = {
  green: 'OK', orange: 'Warning', red: 'Failed', yellow: 'Offline', not_required: 'Not required', missing: 'No report',
};
const REVIEW_LABELS = {
  open: 'Not started', in_review: 'In review', review_complete: 'Review complete', approved: 'Approved',
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : '-');

function StatusChip({ status }) {
  return (
    <Chip
      size="small"
      label={STATUS_LABELS[status] || status}
      sx={{ backgroundColor: STATUS_COLORS[status] || '#eee', color: '#222', fontWeight: 500 }}
    />
  );
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DEADLINE_CHIP = { approved: 'success', on_track: 'default', due_soon: 'warning', overdue: 'error' };

function fmtDue(deadline) {
  if (!deadline) return '';
  const tz = deadline.timezone;
  const opts = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
  try { return new Date(deadline.due_at).toLocaleString('en-GB', tz ? { ...opts, timeZone: tz } : opts) + (tz ? ` ${tz}` : ''); }
  catch (e) { return new Date(deadline.due_at).toLocaleString(); }
}

function download(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// ── Weekly review tab ──────────────────────────────────────────────────────

function WeeklyReview() {
  const { canReviewBackups, email } = usePermissions();
  const canReview = canReviewBackups();

  const [weeks, setWeeks] = useState([]);
  const [week, setWeek] = useState('');
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ customer: '', instance_id: '', status: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, ...}
  const [text, setText] = useState('');
  const [actionForm, setActionForm] = useState({ kind: 'fix', description: '', assigned_to: '', due_date: '', all_types: true });

  const fail = (err, fallback) => setError(err.response?.status === 401 ? 'Your sign-in has expired. You are being taken to sign in again; if nothing happens, sign out and back in.' : (err.response?.data?.detail || fallback));

  const loadWeeks = useCallback(async () => {
    try {
      const res = await backupsAPI.weeks();
      setWeeks(res.data);
      setWeek((current) => current || (res.data[0] ? String(res.data[0].week_start).slice(0, 10) : ''));
    } catch (err) { fail(err, 'Failed to load weeks'); } finally { setLoading(false); }
  }, []);

  const loadWeek = useCallback(async () => {
    if (!week) { setData(null); return; }
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await backupsAPI.week(week, params);
      setData(res.data);
    } catch (err) { fail(err, 'Failed to load the week'); }
  }, [week, filters]);

  useEffect(() => { loadWeeks(); usersAPI.list().then((r) => setUsers(r.data)).catch(() => {}); }, [loadWeeks]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  const run = async (fn, okMessage) => {
    setError(null); setSuccess(null);
    try {
      const res = await fn();
      setSuccess(typeof okMessage === 'function' ? okMessage(res) : okMessage);
      setDialog(null); setText('');
      await Promise.all([loadWeek(), loadWeeks()]);
      return res;
    } catch (err) { fail(err, 'That did not work'); return null; }
  };

  const exportWeek = async (format) => {
    try {
      const params = { format };
      if (filters.customer) params.customer = filters.customer;
      if (filters.instance_id) params.instance_id = filters.instance_id;
      if (filters.status) params.status = filters.status;
      const res = await backupsAPI.exportWeek(week, params);
      download(res.data, `BackupReport_${week}${filters.customer ? '_' + filters.customer : ''}.${format}`);
    } catch (err) { fail(err, 'Export failed'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!weeks.length) {
    return <Alert severity="info">No backup reports have been received yet. Add your SQL Server instances on the Instances tab, then run the job on each.</Alert>;
  }

  const review = data?.review || { status: 'open' };
  const status = review.status;
  const uncovered = data?.uncovered || [];
  const coveredKey = (e) => `${e.instance_id}|${e.database_name}|${e.backup_type}`;
  const uncoveredKeys = new Set(uncovered.map(coveredKey));
  const editable = canReview && (status === 'in_review' || status === 'review_complete');
  const isReviewer = (e) => !!review.reviewer_email && e.toLowerCase() === review.reviewer_email.toLowerCase();
  const myOpenActions = (data?.actions || []).filter((a) => a.status === 'open' && a.assigned_to.toLowerCase() === (email || '').toLowerCase());

  const openActionDialog = (e) => {
    setActionForm({ kind: 'fix', description: '', assigned_to: '', due_date: '', all_types: true });
    setDialog({ type: 'action', exception: e });
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Week starting" value={week} sx={{ minWidth: 260 }}
          onChange={(e) => setWeek(e.target.value)}>
          {weeks.map((w) => {
            const d = String(w.week_start).slice(0, 10);
            return (
              <MenuItem key={d} value={d}>
                {d} — {REVIEW_LABELS[w.status]}{w.deadline && w.deadline.state === 'overdue' ? ' · OVERDUE' : ''}{w.exceptions ? ` · ${w.exceptions} exceptions` : ''}
              </MenuItem>
            );
          })}
        </TextField>
        <Chip label={REVIEW_LABELS[status]} color={status === 'approved' ? 'success' : status === 'open' ? 'default' : 'primary'} />
        {data?.deadline && (
          <Chip
            variant={data.deadline.state === 'on_track' ? 'outlined' : 'filled'}
            color={DEADLINE_CHIP[data.deadline.state]}
            label={data.deadline.state === 'approved' ? 'Approved before the deadline' : `${data.deadline.state === 'overdue' ? 'Overdue, was due' : 'Due'} ${fmtDue(data.deadline)}`}
          />
        )}
        {data && (
          <Typography variant="body2" color="text.secondary">
            {data.submissions.length} of {data.instances.length} instances reported
            {data.open_actions ? ` · ${data.open_actions} open actions` : ''}
            {review.reviewer_email ? ` · reviewer ${review.reviewer_email}` : ''}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" onClick={() => exportWeek('csv')}>Export CSV</Button>
        <Button size="small" variant="outlined" onClick={() => exportWeek('pdf')}>Export PDF</Button>
      </Paper>

      {data && (
        <>
          {status === 'approved' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Approved by {review.approved_by} on {fmt(review.approved_at)}.
              {review.approval_comment ? ` “${review.approval_comment}”` : ''}
              <Box sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>Evidence hash: {review.evidence_hash}</Box>
              {data.later_submissions > 0 && (
                <Box sx={{ mt: 0.5 }}>{data.later_submissions} later report(s) were received after approval and are not part of it.</Box>
              )}
            </Alert>
          )}
          {data.reporting?.missing?.length > 0 && status !== 'approved' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Waiting for {data.reporting.missing.length} of {data.reporting.missing.length + data.reporting.reported.length} instances to report: {data.reporting.missing.join(', ')}.
              The reviewers are emailed as soon as the last one reports.
            </Alert>
          )}
          {data.reporting?.missing?.length === 0 && status === 'open' && (
            <Alert severity="success" sx={{ mb: 2 }}>All {data.reporting.reported.length} instances have reported. The review can start.</Alert>
          )}

          {canReview && status !== 'approved' && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>Review</Typography>
              {status === 'open' && (
                <Button variant="contained" onClick={() => run(() => backupsAPI.startReview(week), 'Review started')}>Start review</Button>
              )}
              {status === 'in_review' && (
                <Button variant="contained" disabled={uncovered.length > 0}
                  onClick={() => run(() => backupsAPI.completeReview(week), 'Review marked complete')}>
                  Mark review complete
                </Button>
              )}
              {status === 'review_complete' && (
                <>
                  <Button variant="contained" color="success" disabled={data.open_actions > 0}
                    onClick={() => { setText(''); setDialog({ type: 'approve' }); }}>
                    Approve week
                  </Button>
                  <Button variant="outlined" color="warning" onClick={() => { setText(''); setDialog({ type: 'return' }); }}>
                    Return to review
                  </Button>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                {status === 'open' && 'Start the review to assign actions for exceptions.'}
                {status === 'in_review' && (uncovered.length ? `${uncovered.length} exceptions still need an action.` : 'Every exception has an action. You can mark the review complete.')}
                {status === 'review_complete' && (data.open_actions ? 'Waiting for open actions to be completed before approval.' : 'All actions are done. Ready for approval.')}
              </Typography>
            </Paper>
          )}

          {data.exceptions.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Exceptions ({data.exceptions.length})</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Instance</TableCell><TableCell>Database</TableCell><TableCell>Backup type</TableCell>
                      <TableCell>Status</TableCell><TableCell>Reason</TableCell><TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.exceptions.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.instance_name}</TableCell>
                        <TableCell>{e.database_name || '(whole instance)'}</TableCell>
                        <TableCell>{e.backup_type || '-'}</TableCell>
                        <TableCell><StatusChip status={e.status} /></TableCell>
                        <TableCell>{e.reason}</TableCell>
                        <TableCell>
                          {uncoveredKeys.has(coveredKey(e))
                            ? (editable ? <Button size="small" onClick={() => openActionDialog(e)}>Assign action</Button> : <Chip size="small" color="warning" label="No action" />)
                            : <Chip size="small" color="success" label="Action assigned" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {data.actions.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Actions ({data.actions.length})</Typography>
              {myOpenActions.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>You have {myOpenActions.length} action(s) assigned to you to complete.</Alert>
              )}
              {data.actions.map((a) => {
                const mine = a.assigned_to.toLowerCase() === (email || '').toLowerCase();
                return (
                  <Box key={a.id} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip size="small" label={a.status === 'done' ? 'Done' : 'Open'} color={a.status === 'done' ? 'success' : 'warning'} />
                      <Chip size="small" variant="outlined" label={a.kind === 'accepted_risk' ? 'Accepted risk' : 'Fix'} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {a.database_name || a.instance_name}{a.backup_type ? ` · ${a.backup_type}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        assigned to {a.assigned_to}{a.due_date ? ` · due ${a.due_date}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{a.description}</Typography>
                    {(a.history || []).map((h) => (
                      <Typography key={h.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {h.event === 'assigned'
                          ? `Assigned to ${h.new_assignee} by ${h.changed_by}`
                          : `Reassigned from ${h.old_assignee} to ${h.new_assignee} by ${h.changed_by}${h.reason ? `: ${h.reason}` : ''}`}
                        {' · '}{fmt(h.changed_at)}
                      </Typography>
                    ))}
                    {a.comments.map((c) => (
                      <Box key={c.id} sx={{ mt: 0.5, pl: 1.5, borderLeft: '3px solid #ccc' }}>
                        <Typography variant="caption" color="text.secondary">{c.author} · {fmt(c.created_at)}</Typography>
                        <Typography variant="body2">{c.comment}</Typography>
                      </Box>
                    ))}
                    {a.status === 'done' && (
                      <Typography variant="caption" color="success.main">Completed by {a.completed_by} on {fmt(a.completed_at)}</Typography>
                    )}
                    {a.status === 'open' && (mine || canReview) && status !== 'approved' && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => { setText(''); setDialog({ type: 'comment', action: a }); }}>Add comment</Button>
                        {canReview && (
                          <Button size="small" onClick={() => { setText(''); setActionForm({ ...actionForm, assigned_to: '' }); setDialog({ type: 'reassign', action: a }); }}>Reassign</Button>
                        )}
                        {mine && (
                          <Button size="small" variant="contained" color="success" onClick={() => { setText(''); setDialog({ type: 'complete', action: a }); }}>
                            Mark complete
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>All databases ({data.results.length})</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField select size="small" label="Customer" sx={{ minWidth: 150 }} value={filters.customer}
                onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}>
                <option value="">All customers</option>
                {data.customers.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__none__">No customer (system)</option>
              </TextField>
              <TextField select size="small" label="Instance" sx={{ minWidth: 220 }} value={filters.instance_id}
                onChange={(e) => setFilters({ ...filters, instance_id: e.target.value })}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}>
                <option value="">All instances</option>
                {data.instances.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </TextField>
              <TextField select size="small" label="Status" sx={{ minWidth: 150 }} value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}>
                <option value="">All statuses</option>
                {['green', 'orange', 'red', 'yellow', 'not_required'].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]} ({data.counts[s] || 0})</option>)}
              </TextField>
              <TextField size="small" label="Search database" value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell><TableCell>Database</TableCell><TableCell>Env</TableCell>
                    <TableCell>Recovery</TableCell><TableCell>State</TableCell><TableCell>AG</TableCell>
                    <TableCell>Backup type</TableCell><TableCell>Last backup</TableCell><TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.customer_code || '-'}</TableCell>
                      <TableCell>{r.database_name}</TableCell>
                      <TableCell>{r.environment || '-'}</TableCell>
                      <TableCell>{r.recovery_model}</TableCell>
                      <TableCell>{r.state}</TableCell>
                      <TableCell>{r.ag_name || 'None'}</TableCell>
                      <TableCell>{r.backup_type}</TableCell>
                      <TableCell>{r.last_backup_at ? String(r.last_backup_at).replace('T', ' ') : 'No Backup'}</TableCell>
                      <TableCell><StatusChip status={r.status} /></TableCell>
                    </TableRow>
                  ))}
                  {data.results.length === 0 && <TableRow><TableCell colSpan={9} align="center">Nothing matches the filters</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Dialogs */}
      <Dialog open={dialog?.type === 'action'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign action</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Alert severity="info">
            Only the person you assign can complete this action, and the week cannot be approved until they do.
            Every assignment and reassignment is recorded in the audit trail.
          </Alert>
          {dialog?.exception && (
            <Typography variant="body2">
              {dialog.exception.instance_name} · {dialog.exception.database_name || 'whole instance'}
              {dialog.exception.backup_type ? ` · ${dialog.exception.backup_type}` : ''} — {dialog.exception.reason}
            </Typography>
          )}
          {dialog?.exception?.database_name && dialog?.exception?.backup_type && (
            <FormControlLabel
              control={<Switch checked={actionForm.all_types} onChange={(e) => setActionForm({ ...actionForm, all_types: e.target.checked })} />}
              label={`Covers every backup type of ${dialog.exception.database_name} (not just ${dialog.exception.backup_type})`}
            />
          )}
          <TextField select label="Type" value={actionForm.kind} onChange={(e) => setActionForm({ ...actionForm, kind: e.target.value })}>
            <MenuItem value="fix">Fix the problem</MenuItem>
            <MenuItem value="accepted_risk">Accept the risk (record why)</MenuItem>
          </TextField>
          <TextField select label="Assign to" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })}>
            {users.filter((u) => u.is_active && !isReviewer(u.email)).map((u) => <MenuItem key={u.id} value={u.email}>{u.name ? `${u.name} (${u.email})` : u.email}</MenuItem>)}
          </TextField>
          {review.reviewer_email && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {review.reviewer_email} is reviewing this week, so actions cannot be assigned to them.
            </Typography>
          )}
          <TextField label="What needs doing" multiline minRows={3} value={actionForm.description}
            onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} />
          <TextField label="Due date (optional)" type="date" value={actionForm.due_date}
            onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={!actionForm.assigned_to || actionForm.description.trim().length < 5}
            onClick={() => run(() => backupsAPI.createAction(week, {
              instance_id: dialog.exception.instance_id,
              database_name: dialog.exception.database_name,
              backup_type: actionForm.all_types ? null : dialog.exception.backup_type,
              kind: actionForm.kind, description: actionForm.description,
              assigned_to: actionForm.assigned_to, due_date: actionForm.due_date || null,
            }), (res) => (res.data.notified
              ? 'Action assigned and the assignee has been emailed'
              : 'Action assigned, but the email could not be sent. Please tell them directly.'))}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'reassign'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign action</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {dialog?.action && (
            <Typography variant="body2">
              {dialog.action.database_name || dialog.action.instance_name}{dialog.action.backup_type ? ` · ${dialog.action.backup_type}` : ''}
              {' '}is currently assigned to <strong>{dialog.action.assigned_to}</strong>. Both people are emailed and the change is recorded with your name and reason.
            </Typography>
          )}
          <TextField select label="Reassign to" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })}>
            {users.filter((u) => u.is_active && !isReviewer(u.email) && u.email.toLowerCase() !== (dialog?.action?.assigned_to || '').toLowerCase())
              .map((u) => <MenuItem key={u.id} value={u.email}>{u.name ? `${u.name} (${u.email})` : u.email}</MenuItem>)}
          </TextField>
          <TextField label="Reason" multiline minRows={2} value={text} onChange={(e) => setText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={!actionForm.assigned_to || text.trim().length < 5}
            onClick={() => run(() => backupsAPI.reassignAction(dialog.action.id, actionForm.assigned_to, text), (res) => (res.data.notified
              ? 'Action reassigned and the new assignee has been emailed'
              : 'Action reassigned, but the email could not be sent. Please tell them directly.'))}>
            Reassign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={['comment', 'complete', 'approve', 'return'].includes(dialog?.type)} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.type === 'comment' && 'Add comment'}
          {dialog?.type === 'complete' && 'Mark action complete'}
          {dialog?.type === 'approve' && `Approve week of ${week}`}
          {dialog?.type === 'return' && 'Return to review'}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {dialog?.type === 'approve' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your approval is recorded against your name and locks this week's evidence. It cannot be edited afterwards.
            </Alert>
          )}
          <TextField fullWidth multiline minRows={3} autoFocus value={text} onChange={(e) => setText(e.target.value)}
            label={dialog?.type === 'complete' ? 'What was done?' : dialog?.type === 'return' ? 'Why is it being returned?' : dialog?.type === 'approve' ? 'Comment (optional)' : 'Comment'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained"
            color={dialog?.type === 'approve' || dialog?.type === 'complete' ? 'success' : 'primary'}
            disabled={(dialog?.type !== 'approve' && text.trim().length < (dialog?.type === 'comment' ? 1 : 5))}
            onClick={() => {
              if (dialog.type === 'comment') run(() => backupsAPI.addComment(dialog.action.id, text), 'Comment added');
              if (dialog.type === 'complete') run(() => backupsAPI.completeAction(dialog.action.id, text), 'Action completed');
              if (dialog.type === 'approve') run(() => backupsAPI.approve(week, text), 'Week approved');
              if (dialog.type === 'return') run(() => backupsAPI.returnWeek(week, text), 'Returned to review');
            }}>
            {dialog?.type === 'approve' ? 'Approve' : dialog?.type === 'return' ? 'Return' : dialog?.type === 'complete' ? 'Complete' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Instances & settings tab (admin) ───────────────────────────────────────

function Instances() {
  const { isAdmin } = usePermissions();
  const [instances, setInstances] = useState([]);
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ full: '', diff: '', log: '', prefixes: '', dweekday: 4, dtime: '17:00', dtz: 'UTC', extra: '' });

  const load = useCallback(async () => {
    try {
      const [i, s] = await Promise.all([backupsAPI.instances(), backupsAPI.settings()]);
      setInstances(i.data); setSettings(s.data);
      setForm({ full: s.data.thresholds.full_hours, diff: s.data.thresholds.diff_hours, log: s.data.thresholds.log_hours, prefixes: s.data.temp_prefixes.join(', '),
        dweekday: s.data.deadline.weekday, dtime: s.data.deadline.time, dtz: s.data.deadline.timezone, extra: (s.data.notify_extra || []).join(', ') });
    } catch (err) { setError(err.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { if (isAdmin()) load(); }, [load, isAdmin]);

  if (!isAdmin()) return <Alert severity="info">Only Cloud Services administrators manage instances and rules.</Alert>;

  const act = async (fn, msg) => {
    setError(null); setSuccess(null);
    try { const res = await fn(); setSuccess(msg); await load(); return res; }
    catch (err) { setError(err.response?.data?.detail || 'That did not work'); return null; }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>SQL Server instances</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" label="Instance name (as reported by @@SERVERNAME)" value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 380 }} />
          <TextField size="small" label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} sx={{ minWidth: 260 }} />
          <Button variant="contained" disabled={!name.trim()}
            onClick={async () => {
              const res = await act(() => backupsAPI.createInstance({ name, description: desc }), 'Instance added');
              if (res) { setNewKey({ name: res.data.name, key: res.data.api_key }); setName(''); setDesc(''); }
            }}>Add instance</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Key</TableCell><TableCell>Last report</TableCell><TableCell>Active</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {instances.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{i.key_prefix}…</TableCell>
                  <TableCell>{fmt(i.last_report_at)}</TableCell>
                  <TableCell>
                    <Switch size="small" checked={i.is_active} onChange={(e) => act(() => backupsAPI.setActive(i.id, e.target.checked), 'Updated')} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={async () => {
                      if (!window.confirm(`Create a new key for ${i.name}? The old key stops working immediately.`)) return;
                      const res = await act(() => backupsAPI.rotateKey(i.id), 'New key created');
                      if (res) setNewKey({ name: res.data.name, key: res.data.api_key });
                    }}>New key</Button>
                  </TableCell>
                </TableRow>
              ))}
              {instances.length === 0 && <TableRow><TableCell colSpan={5} align="center">No instances yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {settings && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Rules</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changes apply to reports received from now on and are recorded in the audit trail.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" type="number" label="Full backup max age (hours)" value={form.full} onChange={(e) => setForm({ ...form, full: e.target.value })} />
            <TextField size="small" type="number" label="Differential max age (hours)" value={form.diff} onChange={(e) => setForm({ ...form, diff: e.target.value })} />
            <TextField size="small" type="number" label="Log max age (hours)" value={form.log} onChange={(e) => setForm({ ...form, log: e.target.value })} />
            <Button variant="outlined" onClick={() => act(() => backupsAPI.putSetting('thresholds', {
              full_hours: parseInt(form.full, 10), diff_hours: parseInt(form.diff, 10), log_hours: parseInt(form.log, 10) }), 'Thresholds saved')}>
              Save thresholds
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField select size="small" label="Review due by" value={form.dweekday} sx={{ minWidth: 160 }}
              onChange={(e) => setForm({ ...form, dweekday: parseInt(e.target.value, 10) })}>
              {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
            </TextField>
            <TextField size="small" type="time" label="Time" value={form.dtime} slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setForm({ ...form, dtime: e.target.value })} />
            <TextField size="small" label="Time zone" value={form.dtz} sx={{ minWidth: 200 }} helperText="e.g. Europe/London, America/New_York, UTC"
              onChange={(e) => setForm({ ...form, dtz: e.target.value })} />
            <Button variant="outlined" onClick={() => act(() => backupsAPI.putSetting('deadline',
              { weekday: form.dweekday, time: form.dtime, timezone: form.dtz.trim() }), 'Deadline saved')}>Save deadline</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" label="Also email these addresses (reviewers are always emailed)" value={form.extra} sx={{ minWidth: 420 }}
              onChange={(e) => setForm({ ...form, extra: e.target.value })} helperText="Comma separated, e.g. a team mailbox" />
            <Button variant="outlined" onClick={() => act(() => backupsAPI.putSetting('notify_extra',
              form.extra.split(',').map((p) => p.trim()).filter(Boolean)), 'Recipients saved')}>Save recipients</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Temporary database prefixes (no backup expected)" value={form.prefixes} sx={{ minWidth: 380 }}
              onChange={(e) => setForm({ ...form, prefixes: e.target.value })} helperText="Comma separated, e.g. TMP_, TEMP_" />
            <Button variant="outlined" onClick={() => act(() => backupsAPI.putSetting('temp_prefixes',
              form.prefixes.split(',').map((p) => p.trim()).filter(Boolean)), 'Prefixes saved')}>Save prefixes</Button>
          </Box>
        </Paper>
      )}

      <Dialog open={!!newKey} onClose={() => setNewKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>API key for {newKey?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Copy this key now. It is shown once and cannot be retrieved later.</Alert>
          <TextField fullWidth value={newKey?.key || ''} slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }} onFocus={(e) => e.target.select()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { navigator.clipboard?.writeText(newKey.key); }}>Copy</Button>
          <Button variant="contained" onClick={() => setNewKey(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Audit tab ──────────────────────────────────────────────────────────────

function Audit() {
  const { canReviewBackups } = usePermissions();
  const [rows, setRows] = useState([]);
  const [verify, setVerify] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canReviewBackups()) return;
    backupsAPI.audit().then((r) => setRows(r.data)).catch((e) => setError(e.response?.data?.detail || 'Failed to load'));
  }, [canReviewBackups]);

  if (!canReviewBackups()) return <Alert severity="info">The audit trail is visible to backup reviewers.</Alert>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={async () => { try { setVerify((await backupsAPI.verifyAudit()).data); } catch (e) { setError('Verification failed'); } }}>
          Verify audit trail integrity
        </Button>
        {verify && (verify.ok
          ? <Alert severity="success" sx={{ py: 0 }}>Intact: {verify.records} records, chain verified.</Alert>
          : <Alert severity="error" sx={{ py: 0 }}>Chain broken at record {verify.first_bad_id}. Escalate to security.</Alert>)}
      </Paper>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Action</TableCell><TableCell>Item</TableCell><TableCell>Detail</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{fmt(r.at)}</TableCell>
                <TableCell>{r.actor}</TableCell>
                <TableCell>{r.action}</TableCell>
                <TableCell>{r.entity} {r.entity_id}</TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 420, wordBreak: 'break-word' }}>{r.detail ? JSON.stringify(r.detail) : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function BackupReports() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h4" gutterBottom>Backup Reports</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weekly review of database backups across all SQL Server instances
      </Typography>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Weekly review" /><Tab label="Instances & rules" /><Tab label="Audit trail" />
      </Tabs>
      {tab === 0 && <WeeklyReview />}
      {tab === 1 && <Instances />}
      {tab === 2 && <Audit />}
    </Box>
  );
}
